import React, { useEffect, useMemo } from 'react';

import { dashboardPaymentSlipBatchApis } from '@apis/next/PaymentSlipBatchApis';
import sortSvg from '@assets/img/icons/sort.svg';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { moneyStrings } from '@data/money';
import { payeeStrings } from '@data/paymentSlip';
import { paymentSlipBatchStrings } from '@data/paymentSlipBatch';
import { useSearch } from '@hooks/next/useSearch';
import {
  PaymentSlipBatchSearchQuery,
  PaymentSlipBatchHelper,
  PaymentSlipBatch,
  PaymentSlipBatchPaymentStatusName,
} from '@models/next/PaymentSlipBatch';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { DEFAULT_PAGE_SIZE, formatDate, formatDateTime, toggleArray } from '@utils/types';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    title: `${paymentSlipBatchStrings.payment_slip_batch}一覧`,
    details: '詳細',
    complete: '完了',
    openDeliveryPriceDialog: '金額入力',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 120px 30px) 90px auto',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  paymentSlipStatusContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
  },
}));

interface PaymentSlipTableProps {
  initialIndex: number;
  items: PaymentSlipBatch[];
}

const PaymentSlipBatchTable: React.FC<PaymentSlipTableProps> = React.memo((props) => {
  const { initialIndex, items } = props;

  const { search, updateSearch } = useSearch(PaymentSlipBatchSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderPaymentStatus = (item: PaymentSlipBatch) => (
    <div className={classes.paymentSlipStatusContainer}>
      <div style={{ width: 'max-content' }}>
        <div>{item.payment_status || '-'}</div>
        {item.withdraw_date && item.payment_status === '済' && (
          <div className={classes.exSmallerText}>{formatDateTime(item.withdraw_date)}</div>
        )}
      </div>
    </div>
  );

  const renderSortButton = (orderName: string) => (
    <img
      src={sortSvg}
      alt="Sort"
      className={classes.baseButton}
      style={{ position: 'relative', top: 3 }}
      onClick={() =>
        updateSearch({
          ordering: toggleArray(search.ordering ?? [], orderName),
        })
      }
    />
  );

  const updateSearchOrRemove = (field: keyof PaymentSlipBatchSearchQuery) => (v: any) =>
    updateSearch({
      page: 1,
      [field]: v ? v : undefined,
    });

  return (
    <Table columnFixedUntil={3} fixedWidths={[60, 120, 80]}>
      <Table.Head>
        <Table.Row
          columns={[
            cellFactory.create('NO.'),
            cellFactory.create(paymentSlipBatchStrings.batch_no),
            cellFactory.createBlank(),
            cellFactory.create(payeeStrings.payee),
            cellFactory.create(
              <>
                {paymentSlipBatchStrings.deadline} {renderSortButton('-deadline')}
              </>,
            ),
            cellFactory.create(
              <>
                {paymentSlipBatchStrings.payment_date} {renderSortButton('-payment_date')}
              </>,
            ),
            cellFactory.create(paymentSlipBatchStrings.totalAmount),
            cellFactory.createSelectable(
              paymentSlipBatchStrings.payment_status,
              [
                {
                  label: paymentSlipBatchStrings.paid,
                  value: PaymentSlipBatchPaymentStatusName.Enum.済,
                },
                {
                  label: paymentSlipBatchStrings.unpaid,
                  value: PaymentSlipBatchPaymentStatusName.Enum.未,
                },
                {
                  label: 'ALL',
                  value: '',
                },
              ],
              updateSearchOrRemove('payment_status'),
            ),
            cellFactory.create(paymentSlipBatchStrings.withdraw_no),
          ]}
        />
      </Table.Head>

      <Table.Body>
        {items.map((item, i) => {
          return (
            <Table.Row
              key={item.id}
              columns={[
                cellFactory.create(<>{initialIndex + i}</>),
                cellFactory.create(<div className={classes.whitespacePre}>{item.batch_no}</div>),
                cellFactory.create(
                  <a
                    href={
                      url('/admin/invoice/PaymentSlipBatchDetails') +
                      '?' +
                      new URLSearchParams({
                        id: String(item.id),
                        batchNo: item.batch_no,
                      }).toString()
                    }
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classes.link}
                  >
                    {strings.details}
                  </a>,
                ),

                cellFactory.create(item.payee.name_en || '-'),
                cellFactory.create(formatDate(item.deadline) || '-'),
                cellFactory.create(
                  <div className={classes.whitespacePre}>
                    {formatDate(item.payment_date) || '-'}
                  </div>,
                ),
                cellFactory.create(
                  <div className={classes.whitespacePre}>
                    {moneyStrings.formatYenFront(new PaymentSlipBatchHelper(item).getTotalAmount())}
                  </div>,
                ),
                cellFactory.create(renderPaymentStatus(item)),
                cellFactory.create(
                  <div className={classes.whitespacePre}>{item.withdraw_no || '-'}</div>,
                ),
              ]}
            />
          );
        })}
      </Table.Body>
    </Table>
  );
});

const DeliveryRequestsSearchForm = () => {
  const { search, updateSearch } = useSearch(PaymentSlipBatchSearchQuery);

  useEffect(() => {
    updateSearch({
      ...search,
      payment_status: '未',
    });
  }, []);

  const initialOutput = useMemo(
    () => ({
      ...search,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    }),
    [search],
  );

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(PaymentSlipBatchSearchQuery, {
    initialOutput,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(pick(fields, ['batch_no', 'payee', 'payment_status']), form),
    [fields],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={paymentSlipBatchStrings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <TextInput
        field={fields.payment_date_after}
        inputProps={{ placeholder: getFieldLabel(fields.payment_date_after) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.payment_date_before}
        inputProps={{ placeholder: getFieldLabel(fields.payment_date_before) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <TextInput
        field={fields.deadline_after}
        inputProps={{ placeholder: getFieldLabel(fields.deadline_after) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.deadline_before}
        inputProps={{ placeholder: getFieldLabel(fields.deadline_before) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <Button text={formStrings.filter} color="primary" type="submit" size="small" shape="round" />
    </form>
  );
};

export const PaymentSlipBatchList: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search: _search } = useSearch(PaymentSlipBatchSearchQuery);

  const search = {
    ..._search,
    page: _search.page ?? 1,
    page_size: _search.page_size ?? DEFAULT_PAGE_SIZE,
  } satisfies PaymentSlipBatchSearchQuery;

  const paymentSlipBatchsQuery = dashboardPaymentSlipBatchApis.useGet(
    '/dashboard/payment-slip-batch',
    {
      queries: search,
    },
  );

  return (
    <Layout title={strings.title}>
      <div className={classes.container}>
        <SectionTitle>{strings.title}</SectionTitle>

        <DeliveryRequestsSearchForm />

        <PageSizeForm />

        <div style={{ height: 16 }} />

        <QueryContainer queries={[paymentSlipBatchsQuery]}>
          <PaymentSlipBatchTable
            initialIndex={1}
            items={paymentSlipBatchsQuery.data?.results ?? []}
          />

          <div className={classes.paginationContainer}>
            <PaginationWithSearchQuery query={paymentSlipBatchsQuery} />
          </div>
        </QueryContainer>
      </div>
    </Layout>
  );
};
